import type {
  CostEstimate,
  FacetCatalog,
  FacetSelection,
  Locale,
  SeniorityTrack,
  StructuredQuery,
} from "@shared/labs/sourcing";
import { ArrowLeft, Coins, Loader2, Plus, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { rpcClient } from "@/lib/rpc";
import { cn } from "@/lib/utils";
import { consolidateArm } from "./lib";

const PAGE_OPTIONS = [2, 4, 6, 8];
const DEFAULT_PAGES = 4;

const LOCALE_OPTIONS: { value: Locale; label: string }[] = [
  { value: "pt-BR", label: "Português (BR)" },
  { value: "es", label: "Español" },
  { value: "en-US", label: "English (US)" },
];

/** minYears → label; the engine maps the number to an upward year-band. */
const MIN_YEARS_OPTIONS: { value: number | undefined; label: string }[] = [
  { value: undefined, label: "Qualquer" },
  { value: 3, label: "3+ anos" },
  { value: 6, label: "6+ anos" },
  { value: 11, label: "11+ anos" },
];

/**
 * "Busca precisa" — Step 2. `understand` proposed the filters; here the recruiter
 * sees EVERY available filter, edits them freely, sizes the run, and launches one
 * deterministic faceted search (no bandit). On start it folds the edits back into
 * the query (a single arm + location/companies/schools) and hands it up.
 */
export function FilterEditor({
  query,
  busy,
  onBack,
  onStart,
}: {
  query: StructuredQuery;
  busy: boolean;
  onBack: () => void;
  onStart: (outQuery: StructuredQuery, pages: number) => void;
}) {
  const [arm, setArm] = useState<FacetSelection>(() => consolidateArm(query));
  const [location, setLocation] = useState(query.filters.location ?? "");
  const [companies, setCompanies] = useState<string[]>(query.filters.companies ?? []);
  const [schools, setSchools] = useState<string[]>(query.filters.schools ?? []);
  const [pages, setPages] = useState(DEFAULT_PAGES);

  const [catalog, setCatalog] = useState<FacetCatalog | null>(null);
  useEffect(() => {
    let cancelled = false;
    rpcClient.labs
      .sourcingFacetCatalog({})
      .then((c) => {
        if (!cancelled) setCatalog(c);
      })
      .catch(() => {
        if (!cancelled) setCatalog(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const industryLabel = useMemo(() => {
    const m = new Map(catalog?.industries.map((o) => [o.value, o.label]));
    return (id: string) => m.get(id) ?? id;
  }, [catalog]);

  const functionLabel = useMemo(() => {
    const m = new Map(catalog?.functions.map((o) => [o.value, o.label]));
    return (id: string) => m.get(id) ?? id;
  }, [catalog]);

  const patch = (p: Partial<FacetSelection>) => setArm((a) => ({ ...a, ...p }));

  const track = arm.seniorityBand?.track;
  function setTrack(t: SeniorityTrack | undefined) {
    if (!t) return patch({ seniorityBand: undefined, excludeManagement: false });
    const opts = catalog?.seniority[t] ?? [];
    const min = opts[0]?.value ?? "";
    const max = opts[opts.length - 1]?.value ?? min;
    patch({ seniorityBand: { track: t, min, max } });
  }

  function start() {
    const outQuery: StructuredQuery = {
      ...query,
      arms: [{ ...arm, title: arm.title.trim() }],
      filters: {
        ...query.filters,
        title: arm.title.trim() || query.filters.title,
        location: location.trim() || undefined,
        companies: companies.length ? companies : undefined,
        schools: schools.length ? schools : undefined,
      },
    };
    onStart(outQuery, pages);
  }

  const seniorityOpts = track ? (catalog?.seniority[track] ?? []) : [];
  const selectedIndustries = arm.industryIds ?? [];
  const selectedFunctions = arm.functionIds ?? [];

  return (
    <section className="space-y-5">
      <div>
        <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          Etapa 02 — os filtros
        </span>
        <h2 className="mt-2 text-xl font-semibold tracking-tight">Revise e ajuste a busca</h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
          O modelo propôs os filtros abaixo a partir do seu brief. Ajuste o que quiser — todos os
          filtros disponíveis estão aqui — e rode <strong>uma busca exata</strong> com exatamente
          esses critérios (sem exploração automática).
        </p>
      </div>

      <div className="space-y-4 rounded-xl border border-border bg-card p-5">
        {/* Title + locale */}
        <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
          <Field label="Título da busca" hint="A frase enviada como busca principal (searchQuery).">
            <Input
              value={arm.title}
              onChange={(e) => patch({ title: e.target.value })}
              placeholder="Ex.: Analista de Folha de Pagamento"
            />
          </Field>
          <Field label="Idioma do título">
            <SearchableSelect
              value={arm.locale ?? ""}
              onValueChange={(v) => patch({ locale: (v || undefined) as Locale | undefined })}
              options={LOCALE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              placeholder="Automático"
              triggerClassName="min-w-[180px]"
            />
          </Field>
        </div>

        {/* Current job titles + location */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Cargos atuais (OR)"
            hint="Títulos exatos/adjacentes — amplia a busca (currentJobTitles)."
          >
            <ChipInput
              values={arm.currentJobTitles ?? []}
              onChange={(v) => patch({ currentJobTitles: v.length ? v : undefined })}
              placeholder="Adicionar cargo e Enter"
            />
          </Field>
          <Field label="Localização" hint="Nome real do local no LinkedIn (sem 'remoto').">
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Ex.: São Paulo, Brazil"
            />
          </Field>
        </div>

        {/* Seniority */}
        <Field
          label="Senioridade"
          hint="Duas trilhas ordinais: contribuidor individual (IC) ou gestão."
        >
          <div className="flex flex-wrap items-center gap-3">
            <Segmented
              value={track ?? "none"}
              options={[
                { value: "none", label: "Qualquer" },
                { value: "ic", label: "IC" },
                { value: "mgmt", label: "Gestão" },
              ]}
              disabled={busy}
              onChange={(v) => setTrack(v === "none" ? undefined : (v as SeniorityTrack))}
            />
            {track && (
              <div className="flex items-center gap-2">
                <SearchableSelect
                  value={arm.seniorityBand?.min ?? ""}
                  onValueChange={(v) =>
                    arm.seniorityBand && patch({ seniorityBand: { ...arm.seniorityBand, min: v } })
                  }
                  options={seniorityOpts}
                  placeholder="mín."
                  triggerClassName="min-w-[150px]"
                />
                <span className="text-muted-foreground">→</span>
                <SearchableSelect
                  value={arm.seniorityBand?.max ?? ""}
                  onValueChange={(v) =>
                    arm.seniorityBand && patch({ seniorityBand: { ...arm.seniorityBand, max: v } })
                  }
                  options={seniorityOpts}
                  placeholder="máx."
                  triggerClassName="min-w-[150px]"
                />
              </div>
            )}
            {track === "ic" && (
              <label className="inline-flex items-center gap-2 text-sm">
                <Checkbox
                  checked={!!arm.excludeManagement}
                  onCheckedChange={(c) => patch({ excludeManagement: c === true })}
                />
                Excluir gestão (mantém só ICs)
              </label>
            )}
          </div>
        </Field>

        {/* Function + min years */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Funções"
            hint="OR entre funções (functionIds) — adicione adjacentes p/ papéis híbridos."
          >
            <SearchableSelect
              value=""
              onValueChange={(id) => {
                if (!selectedFunctions.includes(id))
                  patch({ functionIds: [...selectedFunctions, id] });
              }}
              options={(catalog?.functions ?? []).filter(
                (o) => !selectedFunctions.includes(o.value),
              )}
              placeholder="Adicionar função…"
              searchPlaceholder="Buscar função…"
            />
            {selectedFunctions.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {selectedFunctions.map((id) => (
                  <RemovableChip
                    key={id}
                    label={functionLabel(id)}
                    onRemove={() =>
                      patch({
                        functionIds: selectedFunctions.filter((x) => x !== id).length
                          ? selectedFunctions.filter((x) => x !== id)
                          : undefined,
                      })
                    }
                  />
                ))}
              </div>
            )}
          </Field>
          <Field label="Experiência mínima" hint="Vira uma faixa aberta para cima de anos.">
            <Segmented
              value={String(arm.minYears ?? "")}
              options={MIN_YEARS_OPTIONS.map((o) => ({
                value: String(o.value ?? ""),
                label: o.label,
              }))}
              disabled={busy}
              onChange={(v) => patch({ minYears: v ? Number(v) : undefined })}
            />
          </Field>
        </div>

        {/* Industries */}
        <Field label="Setores" hint="OR entre setores (industryIds) — adicione os relevantes.">
          <SearchableSelect
            value=""
            onValueChange={(id) => {
              if (!selectedIndustries.includes(id))
                patch({ industryIds: [...selectedIndustries, id] });
            }}
            options={(catalog?.industries ?? []).filter(
              (o) => !selectedIndustries.includes(o.value),
            )}
            placeholder="Adicionar setor…"
            searchPlaceholder="Buscar setor…"
          />
          {selectedIndustries.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {selectedIndustries.map((id) => (
                <RemovableChip
                  key={id}
                  label={industryLabel(id)}
                  onRemove={() =>
                    patch({
                      industryIds: selectedIndustries.filter((x) => x !== id).length
                        ? selectedIndustries.filter((x) => x !== id)
                        : undefined,
                    })
                  }
                />
              ))}
            </div>
          )}
        </Field>

        {/* Company headcount */}
        <Field label="Tamanho da empresa atual" hint="Faixas de headcount (companyHeadcount).">
          <div className="flex flex-wrap gap-1.5">
            {(catalog?.headcount ?? []).map((o) => {
              const on = (arm.companyHeadcount ?? []).includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    const cur = arm.companyHeadcount ?? [];
                    const next = on ? cur.filter((x) => x !== o.value) : [...cur, o.value];
                    patch({ companyHeadcount: next.length ? next : undefined });
                  }}
                  className={cn(
                    "rounded-full border px-2.5 py-[3px] text-xs font-medium transition-colors",
                    on
                      ? "border-foreground bg-foreground text-background"
                      : "border-border bg-background text-muted-foreground hover:text-foreground",
                  )}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </Field>

        {/* Companies + schools */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Empresas-alvo" hint="Nomes de empresas (resolvidos para URLs do LinkedIn).">
            <ChipInput
              values={companies}
              onChange={setCompanies}
              placeholder="Adicionar empresa e Enter"
            />
            {companies.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-4">
                <label className="inline-flex items-center gap-2 text-xs">
                  <Checkbox
                    checked={!!arm.useCompanies}
                    onCheckedChange={(c) => patch({ useCompanies: c === true })}
                  />
                  como empregador atual
                </label>
                <label className="inline-flex items-center gap-2 text-xs">
                  <Checkbox
                    checked={!!arm.usePastCompanies}
                    onCheckedChange={(c) => patch({ usePastCompanies: c === true })}
                  />
                  como empregador anterior
                </label>
              </div>
            )}
          </Field>
          <Field label="Instituições de ensino" hint="Nomes de escolas/universidades.">
            <ChipInput
              values={schools}
              onChange={setSchools}
              placeholder="Adicionar escola e Enter"
            />
            {schools.length > 0 && (
              <label className="mt-2 inline-flex items-center gap-2 text-xs">
                <Checkbox
                  checked={!!arm.useSchools}
                  onCheckedChange={(c) => patch({ useSchools: c === true })}
                />
                filtrar por estas escolas
              </label>
            )}
          </Field>
        </div>

        {/* Recently changed jobs */}
        <label className="inline-flex items-center gap-2 text-sm">
          <Checkbox
            checked={!!arm.recentlyChangedJobs}
            onCheckedChange={(c) => patch({ recentlyChangedJobs: c === true })}
          />
          Apenas quem trocou de emprego recentemente (~90 dias)
        </label>
      </div>

      {/* Page budget + estimate + launch */}
      <SearchPanel pages={pages} setPages={setPages} busy={busy} onStart={start} onBack={onBack} />
    </section>
  );
}

// ─── Page budget + cost estimate + launch ───────────────────────────────────

function SearchPanel({
  pages,
  setPages,
  busy,
  onStart,
  onBack,
}: {
  pages: number;
  setPages: (p: number) => void;
  busy: boolean;
  onStart: () => void;
  onBack: () => void;
}) {
  const [cost, setCost] = useState<CostEstimate | null>(null);
  const [estimating, setEstimating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setEstimating(true);
    rpcClient.labs
      .sourcingPreciseEstimate({ pages })
      .then((c) => {
        if (!cancelled) setCost(c);
      })
      .catch(() => {
        if (!cancelled) setCost(null);
      })
      .finally(() => {
        if (!cancelled) setEstimating(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pages]);

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        páginas a buscar
      </div>
      <Segmented
        value={String(pages)}
        options={PAGE_OPTIONS.map((p) => ({ value: String(p), label: String(p) }))}
        disabled={busy}
        onChange={(v) => setPages(Number(v))}
      />
      <p className="mt-1.5 max-w-xl text-xs leading-relaxed text-muted-foreground">
        Cada página traz ~25 perfis. Mais páginas = lista maior, a um custo proporcional do Apify.
      </p>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4 rounded-lg border border-border bg-muted/20 px-4 py-3">
        <div className="flex items-center gap-2">
          <Coins size={14} className="text-amber-500" />
          <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            custo apify estimado
          </span>
        </div>
        <div className="flex items-center gap-5">
          <div className="text-right">
            <div className="font-mono text-sm font-semibold tabular-nums">
              {cost ? cost.pages : "—"}
            </div>
            <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
              páginas
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-sm font-semibold tabular-nums">
              {cost ? `≤ ${cost.maxProfiles}` : "—"}
            </div>
            <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
              perfis
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-2xl font-semibold leading-none tabular-nums">
              <span className="text-muted-foreground">até $</span>
              {cost ? cost.usd.toFixed(2) : estimating ? "…" : "0.00"}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Button variant="outline" onClick={onBack} disabled={busy}>
          <ArrowLeft /> Voltar
        </Button>
        <Button onClick={onStart} disabled={busy}>
          {busy ? <Loader2 className="animate-spin" /> : <Search />}
          Buscar candidatos
        </Button>
      </div>
    </div>
  );
}

// ─── Small building blocks ──────────────────────────────────────────────────

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">{label}</Label>
      {children}
      {hint && <p className="text-[11px] leading-snug text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Segmented({
  value,
  options,
  disabled,
  onChange,
}: {
  value: string;
  options: { value: string; label: string }[];
  disabled?: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <div className="inline-flex flex-wrap rounded-lg border border-border bg-background p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded-md px-3 py-1 text-sm font-medium tabular-nums transition-colors",
            value === o.value
              ? "bg-muted text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function RemovableChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <Badge variant="secondary" className="gap-1 pr-1 font-normal">
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="rounded-full hover:bg-muted-foreground/20"
      >
        <X className="h-3 w-3" />
      </button>
    </Badge>
  );
}

/** A free-text → chips input: type a value, Enter (or the + button) adds it. */
function ChipInput({
  values,
  onChange,
  placeholder,
}: {
  values: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setDraft("");
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
        />
        <Button
          variant="outline"
          size="icon"
          onClick={add}
          disabled={!draft.trim()}
          aria-label="Adicionar"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((v) => (
            <RemovableChip
              key={v}
              label={v}
              onRemove={() => onChange(values.filter((x) => x !== v))}
            />
          ))}
        </div>
      )}
    </div>
  );
}
